/**
 * Credits API
 * Handles credit packages, purchases, balance, and history
 * Endpoints: /api/credits/*
 */
import apiClient, { createAuthConfig, isMockApi } from "../client";
import {
  mockGetPackages,
  mockGetBalance,
  mockGetCreditSummary,
  mockGetHistory,
  mockPurchasePackage,
  mockAddCredits,
  mockRequestMoreCredits,
} from "../mocks";
import { buildStaticPricingCatalog } from "../../../data/pricingModal";

/**
 * Get available credit packages
 * @returns {Promise<Object>} - { success, packages, total }
 */
export const getPackages = async () => {
  if (isMockApi) return mockGetPackages();
  const response = await apiClient.get('/api/credits/packages');
  return response.data;
};

/**
 * Unified pricing catalog (packs, plans, per-tool credit costs).
 */
export const getPricing = async () => {
  if (isMockApi) {
    return buildStaticPricingCatalog();
  }
  const response = await apiClient.get("/api/pricing");
  return response.data;
};

/**
 * Purchase a credit package
 * @param {string} token - Clerk session token
 * @param {number} packageId - Credit package ID
 * @returns {Promise<Object>} - Purchase response
 */
export const purchasePackage = async (token, packageId) => {
  if (isMockApi) return mockPurchasePackage();
  const response = await apiClient.post(
    '/api/credits/purchase',
    { package_id: packageId },
    createAuthConfig(token)
  );
  return response.data;
};

/**
 * Get user's current credit balance
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>} - { success, credits, clerk_id }
 */
export const getBalance = async (token) => {
  if (isMockApi) return mockGetBalance();
  const response = await apiClient.get(
    '/api/credits/balance',
    { ...createAuthConfig(token), timeout: 30000 }
  );
  return response.data;
};

/**
 * Lifetime vs monthly credits breakdown from ledger.
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>} - { success, lifetime_credits, monthly_credits }
 */
export const getCreditSummary = async (token) => {
  if (isMockApi) return mockGetCreditSummary();
  const response = await apiClient.get(
    '/api/credits/summary',
    createAuthConfig(token)
  );
  return response.data;
};

/**
 * Get user's credit transaction history
 * @param {string} token - Clerk session token
 * @param {number} limit - Maximum number of transactions
 * @returns {Promise<Object>} - { success, transactions, total }
 */
export const getHistory = async (token, limit = 50) => {
  if (isMockApi) return mockGetHistory();
  const params = new URLSearchParams({ limit: limit.toString() });
  const response = await apiClient.get(`/api/credits/history?${params}`, createAuthConfig(token));
  return response.data;
};

/**
 * Add credits to user account (admin function)
 * @param {string} token - Clerk session token
 * @param {number} amount - Amount of credits to add
 * @param {string} source - Source of credits (default: 'admin')
 * @param {string} referenceId - Optional reference ID
 * @returns {Promise<Object>}
 */
export const addCredits = async (token, amount, source = 'admin', referenceId = null) => {
  if (isMockApi) return mockAddCredits();
  const params = new URLSearchParams({
    amount: amount.toString(),
    source,
  });
  if (referenceId) {
    params.append('reference_id', referenceId);
  }
  const response = await apiClient.post(`/api/credits/add?${params}`, {}, createAuthConfig(token));
  return response.data;
};

/**
 * Request more credits during early access (pre-payment gateway).
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>}
 */
export const requestMoreCredits = async (token) => {
  if (isMockApi) return mockRequestMoreCredits();
  const response = await apiClient.post(
    '/api/request-credits',
    {},
    createAuthConfig(token)
  );
  return response.data;
};

export default {
  getPackages,
  getPricing,
  purchasePackage,
  getBalance,
  getCreditSummary,
  getHistory,
  addCredits,
  requestMoreCredits,
};





